import React, { useState } from "react";
import { Reveal, Overline } from "./Motion";
import { SmartImage } from "./SmartImage";
import { VERIFIED } from "@/data/cafe";
import { submitFranchise } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const field = "rounded-sm border-white/15 bg-white/5 text-[#F5F2EB] placeholder:text-[#F5F2EB]/40 focus-visible:ring-[#1C7C54] focus-visible:ring-1 focus-visible:ring-offset-0 font-body";
const labelCls = "font-accent text-xs uppercase tracking-[0.15em] text-[#F5F2EB]/60 mb-1.5 block";

const stats = [
  { n: "40+", l: "Outlets" },
  { n: "12", l: "Cities" },
  { n: "4.9★", l: "Avg. Rating" },
];

export default function Franchise() {
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const f = e.target;
    setLoading(true);
    try {
      await submitFranchise({
        name: f.name.value, phone: f.phone.value, email: f.email.value,
        city: f.city.value, investment_range: f.investment.value || null, message: f.message.value || null,
      });
      toast.success("Thanks! Our franchise team will reach out soon. ☕");
      f.reset();
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <section id="franchise" className="relative bg-[#2C2523] py-24 md:py-32 overflow-hidden" data-testid="franchise-section">
      <div className="absolute inset-0 opacity-20">
        <SmartImage src={VERIFIED.interior2} alt="" className="h-full w-full object-cover" />
      </div>
      <div className="absolute inset-0 bg-[#2C2523]/85" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div>
          <Overline>Franchise</Overline>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#F5F2EB] mt-4 font-medium leading-[1.05]">
            Open a <span className="italic text-[#1C7C54]">Teaavi Coffee</span> in your city
          </h2>
          <p className="font-body text-[#F5F2EB]/70 text-lg leading-relaxed mt-6 max-w-lg">
            Join a fast-growing community of coffee entrepreneurs. We bring the brand, the beans, and the playbook —
            you bring the passion. Become our franchise partner today.
          </p>
          <div className="flex gap-10 mt-10">
            {stats.map((s) => (
              <div key={s.l}>
                <p className="font-display text-4xl md:text-5xl text-[#1C7C54]">{s.n}</p>
                <p className="overline text-[#F5F2EB]/60 mt-1">{s.l}</p>
              </div>
            ))}
          </div>
        </div>

        <Reveal>
          <form onSubmit={submit} className="bg-[#1A1514]/60 backdrop-blur-md border border-white/10 rounded-sm p-7 md:p-9 space-y-4" data-testid="franchise-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className={labelCls}>Name</Label><Input name="name" required className={field} data-testid="fr-name" /></div>
              <div><Label className={labelCls}>Phone</Label><Input name="phone" required className={field} data-testid="fr-phone" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className={labelCls}>Email</Label><Input name="email" type="email" required className={field} data-testid="fr-email" /></div>
              <div><Label className={labelCls}>City</Label><Input name="city" required className={field} data-testid="fr-city" /></div>
            </div>
            <div><Label className={labelCls}>Investment Range</Label><Input name="investment" placeholder="$50k – $150k" className={field} data-testid="fr-investment" /></div>
            <div><Label className={labelCls}>Message</Label><Textarea name="message" rows={3} className={field} data-testid="fr-message" /></div>
            <Button type="submit" disabled={loading} data-testid="fr-submit"
              className="w-full h-11 rounded-sm bg-[#1C7C54] text-[#F5F2EB] hover:bg-[#14603F] font-accent uppercase tracking-[0.15em] text-xs transition-colors">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Become a Partner"}
            </Button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
